/**
 * A project's site as a place: what the address suggestions knew when it was
 * picked. A site can be as exact as a street address or as loose as a state;
 * typed text that matched nothing stays text (site_address) with no place.
 */

export const SITE_PRECISIONS = ['address', 'street', 'locality', 'county', 'region', 'country'] as const;
export type SitePrecision = (typeof SITE_PRECISIONS)[number];

export interface PlaceParts {
  houseNumber: string | null;
  street: string | null;
  locality: string | null;
  county: string | null;
  region: string | null;
  postcode: string | null;
  country: string | null;
  countryCode: string | null;
}

export interface SitePlace {
  /** The address as the lookup writes it: Google's own writing of it (its formatted address), or Photon's. */
  label: string;
  precision: SitePrecision;
  /** WGS84. Kept (Jason, 2026-10-27: "we can store the coordinates because they are coordinates and this is earth"). */
  lat: number;
  lng: number;
  parts: PlaceParts;
  /** Google Places once its key is connected (2026-10-27), Photon (OpenStreetMap) before and without it. */
  source: 'photon' | 'google';
  /** Google's id for the place, for NSB's own map link; Photon's have none. */
  placeId?: string | null;
}

/**
 * One suggestion for the words typed. Photon's is the place itself; Google's is its words and id until it is picked,
 * when the place is asked for in the same typing session (`GET /api/places/pick`).
 */
export interface PlaceSuggestion {
  label: string;
  /** How exact it is, when known before it is picked. */
  precision: SitePrecision | null;
  place: SitePlace | null;
  placeId: string | null;
}

export interface PlaceSuggestionsDto {
  suggestions: PlaceSuggestion[];
  /** The lookup service did not answer; typing the site still works. */
  unavailable?: boolean;
}

/** One word for how exact a place is, for the suggestion list. */
export function precisionWord(p: SitePrecision): string {
  return { address: 'Address', street: 'Road', locality: 'Town', county: 'County', region: 'State', country: 'Country' }[p];
}

/**
 * The place without the street: "Fletcher, Vermont". This is what a client
 * sees beside the project title and the only part of a site that leaves the
 * portal for research. A house number never does.
 */
export function placeName(site: SitePlace): string {
  const { locality, county, region, country, countryCode } = site.parts;
  const local = locality ?? (site.precision === 'county' || site.precision === 'address' || site.precision === 'street' ? county : null);
  const abroad = countryCode && countryCode.toUpperCase() !== 'US' ? country : null;
  const parts = [local, region, abroad].filter((x): x is string => Boolean(x));
  return parts.length ? parts.join(', ') : site.label;
}

/**
 * Which projects share research: the same town (or county, or state) is the
 * same landscape, so a house and a barn on one property are researched once.
 */
export function placeKey(site: SitePlace): string {
  const { locality, county, region, countryCode } = site.parts;
  return [countryCode ?? '', region ?? '', locality ?? county ?? '']
    .map((s) => s.trim().toLowerCase().replace(/\s+/g, '-'))
    .join('/');
}

/** The key for a site that was only typed, never picked: the words themselves. */
export function typedPlaceKey(text: string): string {
  return `typed/${text.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
}

const US_STATES = new Set(
  'AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' '),
);
/** Short words an address writes in capitals wherever they stand: a post office box, a US route, a rural route, the compass. */
const UPPER = new Set(['po', 'us', 'rr', 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']);
/** Small words kept small inside a name (Road of the Pines), never as its first word. */
const SMALL = new Set(['of', 'the', 'and', 'at', 'on', 'in', 'by', 'to', 'for', 'de', 'la', 'du']);
const ZIP = /^\d{5}(?:-\d{4})?$/;

/**
 * A site typed and never picked, in title case (P-35, Jason on Kalley's header: "be title case"): each word with its
 * first letter up, a state's two letters, the compass and the like in capitals, a unit's letter after its number up
 * and an ordinal's down (12B, 2nd), small words small inside a name. A word cased on purpose (McAllister), or in
 * capitals among words that are not, is kept as written. A state's letters are only read as one where a state goes:
 * after a comma, or before a ZIP code.
 */
export function siteWords(text: string): string {
  const t = text.trim().replace(/\s+/g, ' ');
  if (!t) return '';
  const shouting = !/[a-z]/.test(t);
  const parts = t.split(',').map((p) => p.trim()).filter(Boolean);
  return parts
    .map((part, pi) => {
      const tokens = part.split(' ');
      let first = true;
      return tokens
        .map((tok, ti) => {
          const lead = /^[^A-Za-z0-9]*/.exec(tok)![0];
          const rest = tok.slice(lead.length);
          const trail = /[^A-Za-z0-9]*$/.exec(rest)![0];
          const core = rest.slice(0, rest.length - trail.length);
          if (!core) return tok;
          const firstWord = first && /^[A-Za-z]/.test(core);
          if (/^[A-Za-z]/.test(core)) first = false;
          const statePlace = (pi > 0 && (tokens.length === 1 || ti === tokens.length - 1)) || ZIP.test(tokens[ti + 1] ?? '');
          return lead + word(core, { shouting, firstWord, statePlace }) + trail;
        })
        .join(' ');
    })
    .join(', ');
}

function word(core: string, at: { shouting: boolean; firstWord: boolean; statePlace: boolean }): string {
  if (/^\d/.test(core)) return /^\d+(?:st|nd|rd|th)$/i.test(core) ? core.toLowerCase() : core.toUpperCase();
  const lower = /[a-z]/.test(core);
  const upper = /[A-Z]/.test(core);
  if (lower && upper) return core;
  if (upper && !at.shouting) return core;
  const low = core.toLowerCase();
  if (at.statePlace && /^[a-z]{2}$/.test(low) && US_STATES.has(low.toUpperCase())) return low.toUpperCase();
  if (UPPER.has(low)) return low.toUpperCase();
  if (SMALL.has(low) && !at.firstWord) return low;
  // Each piece of a joined name up (Smith-Jones), and the name after an O' or a D' (O'Brien), not a possessive's s.
  return low.replace(/(^|-)([a-z])/g, (_m, sep: string, c: string) => sep + c.toUpperCase()).replace(/^([A-Za-z])['’]([a-z])/, (_m, a: string, b: string) => `${a}'${b.toUpperCase()}`);
}

/** NSB's line for a project's site (P-35): the address as picked, Google's own writing of it; else as typed, in title case. */
export function siteLine(s: { siteAddress: string | null; sitePlace: SitePlace | null }): string | null {
  if (s.sitePlace) return s.sitePlace.label;
  const words = s.siteAddress?.trim();
  return words ? siteWords(words) : null;
}

/**
 * NSB's own link from a site to Google Maps (P-35: "a map icon jump link to its location"): a picked place by its
 * coordinates, with Google's id when it has one; a site only typed by its words. Never a client's: they never see the
 * street (CLAUDE.md, the site rule).
 */
export function mapLink(s: { siteAddress: string | null; sitePlace: SitePlace | null }): string | null {
  const base = 'https://www.google.com/maps/search/?api=1';
  if (s.sitePlace) {
    const id = s.sitePlace.placeId ? `&query_place_id=${encodeURIComponent(s.sitePlace.placeId)}` : '';
    return `${base}&query=${encodeURIComponent(`${s.sitePlace.lat},${s.sitePlace.lng}`)}${id}`;
  }
  const words = s.siteAddress?.trim();
  return words ? `${base}&query=${encodeURIComponent(words)}` : null;
}
